'use client';

import './article-template.scss';

import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { keys } from '@sola/contentful-model/keys.js';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.js';
import { programCountryLabels } from '@sola/contentful-model/lib/sac-values.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import {
  TrackFunnelPage,
  TrackViewContent,
} from '@sola/cookies/components/datalayer.jsx';
import { ArticleProps } from '@sola/schema/lib/schema.js';
import { submitSubscription } from '@sola/skippy-sdk/requests.js';
import { slugify } from '@sola/utils/string.js';

import { Image } from '../../atoms/image/image';
import {
  FSmeta,
  FSmetaFullBleed,
  FSmetaFullBleedLight,
} from '../../mocks/mocks';
import { AccordionMain } from '../../organisms-cms/accordion/main/accordion-main';
import { ArticleInsert } from '../../organisms-cms/article-insert/article-insert';
import { BasicContentMain } from '../../organisms-cms/basic-content/main/basic-content-main';
import { BasicContentSolo } from '../../organisms-cms/basic-content/solo/basic-content-solo';
import { DonateShim } from '../../organisms-cms/donate/shim/donate-shim';
import { GalleryCinema } from '../../organisms-cms/gallery/cinema/gallery-cinema';
import { JumbotronNotable } from '../../organisms-cms/jumbotron/notable/jumbotron-notable';
import { JumbotronSidekick } from '../../organisms-cms/jumbotron/sidekick/jumbotron-sidekick';
import { FlexibleSection } from '../../organisms/flexible-section/flexible-section';
import { FormEmailSubscribe } from '../../organisms/forms/form-email-subscribe/form-email-subscribe';

interface ArticleTemplateProps {
  article: ArticleProps;
}

const ArticleSectionsEnum = z.enum(['cta', 'related', 'faq']);
type ArticleSectionsEnum = z.infer<typeof ArticleSectionsEnum>;

const ArticleJumpNav = ({ sections, taxonomyArticleType }: ArticleProps) => {
  const { t } = useTranslation();

  return (
    <div className="Article__sidebar-box pb-comfy-xl grid gap-comfy-sm">
      <h2 className="heading heading-xs heading-amplified">
        {taxonomyArticleType === keys.articleTypes.crisisUpdate
          ? t('skippy.article.updates')
          : t('skippy.article.tableOfContents')}
      </h2>
      <div className="Article__sections flex flex-col gap-comfy-xs">
        {sections?.map((section) => (
          <Link key={section.id} href={`#${section.id}`}>
            <h3 className="label-lg label-potent">{section.title}</h3>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const ArticleTemplate: React.FC<ArticleTemplateProps> = ({
  article,
}) => {
  const { t } = useTranslation();

  const articleBase = article.taxonomyArticleType
    ? articlesUrlBase[article.taxonomyArticleType as ArticleTypesEnum]
    : '#';

  const renderTaxonomyTags = (firstOnly = false) => {
    const tags: ReactNode[] = [];

    // Type
    tags.push(
      <Link
        key="type"
        href={`${articleBase}`}
        title={t(
          `skippy.taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`,
        )}
      >
        <Badge
          label={t(
            `skippy.taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`,
            'skippy.taxonomy.articleTypes.type.singularName',
          )}
        />
      </Link>,
    );

    // Topics
    const topics = firstOnly
      ? article.taxonomyTopic?.slice(0, 1)
      : article.taxonomyTopic;
    topics?.forEach((topic) => {
      tags.push(
        <Link
          key={`topic-${topic}`}
          href={`${articleBase}/tag/${slugify(topic)}`}
          title={t(`skippy.taxonomy.articleTopics.${topic}.name`)}
        >
          <Badge
            label={t(
              `skippy.taxonomy.articleTopics.${topic}.name`,
              'skippy.taxonomy.articleTopics.topic.name',
            )}
          />
        </Link>,
      );
    });

    // Countries
    const countries = firstOnly
      ? article.taxonomyProgramCountry?.slice(0, 1)
      : article.taxonomyProgramCountry;
    countries?.forEach((country) => {
      const countryLabel =
        programCountryLabels[country as keyof typeof programCountryLabels];
      tags.push(
        <Link
          key={`country-${country}`}
          href={`${articleBase}/tag/${slugify(countryLabel)}`}
          title={t(`geo.countries.${country}.nameS`)}
        >
          <Badge label={t(`geo.countries.${country}.nameS`)} />
        </Link>,
      );
    });

    // Themes
    const themes = firstOnly
      ? article.taxonomyArticleTheme?.slice(0, 1)
      : article.taxonomyArticleTheme;
    themes?.forEach((theme) => {
      tags.push(
        <Link
          key={`theme-${theme}`}
          href={`${articleBase}/tag/${slugify(theme)}`}
          title={t(`skippy.taxonomy.articleThemes.${theme}.name`)}
        >
          <Badge
            label={t(
              `skippy.taxonomy.articleThemes.${theme}.name`,
              'skippy.taxonomy.articleThemes.theme.name',
            )}
          />
        </Link>,
      );
    });

    return tags;
  };

  // controls section colors
  const activeSections: ArticleSectionsEnum[] = [];
  if (article.secondaryCta) {
    activeSections.push(ArticleSectionsEnum.enum.cta);
  }

  activeSections.push(ArticleSectionsEnum.enum.related);

  if (article.faq) {
    activeSections.push(ArticleSectionsEnum.enum.faq);
  }

  const getSectionMeta = (section: string) => {
    const index = activeSections.indexOf(section as ArticleSectionsEnum);
    return {
      ...(index % 2 === 0 ? FSmetaFullBleedLight : FSmetaFullBleed),
      anchor: section,
    };
  };

  const hasJumpList = !!article.sections;

  return (
    <div className="Article Article--blog ">
      <TrackFunnelPage funnel={article.taxonomyFunnel} />
      <TrackViewContent
        cmsId={article.contentfulEntryID!}
        title={article.title}
        type={article.taxonomyArticleType}
        tags={[
          ...(article.taxonomyArticleTheme ?? []),
          ...(article.taxonomyTopic ?? []),
        ]}
      />
      <FlexibleSection
        meta={{ ...FSmeta, anchor: 'intro' }}
        children1={
          <div className="Article__intro">
            <BasicContentSolo
              seoLevel={1}
              subtitle={
                article.publishDate
                  ? new Date(article.publishDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })
                  : undefined
              }
              title={article.title || undefined}
              body={<p>{article.summary}</p>}
            />

            <div className="Article__metabar flex justify-between w-full">
              <div className="Article__tags flex flex-wrap gap-snug-xl">
                {renderTaxonomyTags(true)}
              </div>
              <div className="Article__meta-bar__share">
                {/* <FeatureButtons variant="noWrap">
                <ButtonStyle variant="secondary" iconBefore={<IconHeart />}>
                  <button type="button">Like</button>
                </ButtonStyle>
                <ButtonStyle variant="secondary" iconBefore={<IconHeart />}>
                  <button type="button">Share</button>
                </ButtonStyle>
              </FeatureButtons> */}
              </div>
            </div>

            {article.primaryMedia?.items && (
              <div className="Article__primary-media">
                <GalleryCinema {...article.primaryMedia} />
              </div>
            )}
          </div>
        }
      />
      <FlexibleSection
        meta={{
          ...FSmeta,
          overlap:
            article.primaryMedia?.items &&
            article.primaryMedia.items.length > 0,
          anchor: 'article',
          layoutRatio: '2-1',
        }}
        //  @TODO: bring back table of contents
        //  featureOnMobile={
        //     hasJumpList ? <ArticleJumpNav {...article} /> : undefined
        //  }
        children1={
          <article className="Article__article body-md flex flex-col gap-comfy-xl">
            {article.intro && (
              <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl">
                <BasicContentMain seoLevel={2} body={article.intro} inline />
              </div>
            )}

            <BasicContentMain
              seoLevel={2}
              body={article.body || undefined}
              inline
            />

            {article.conclusion &&
              article.taxonomyArticleType !==
                keys.articleTypes.crisisUpdate && (
                <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl">
                  <BasicContentMain
                    seoLevel={2}
                    body={article.conclusion}
                    inline
                  />
                </div>
              )}

            {article.conclusion &&
              article.taxonomyArticleType ===
                keys.articleTypes.crisisUpdate && (
                <BasicContentMain
                  seoLevel={2}
                  body={article.conclusion}
                  inline
                />
              )}

            <div className="Article__footer mt-comfy-sm">
              <div className="Article__tags flex flex-wrap gap-snug-xl">
                {renderTaxonomyTags(false)}
              </div>
              <div className="Article__share">{/* TODO: share article */}</div>
            </div>
          </article>
        }
        children2={
          <aside className="Article__sidebar flex flex-col gap-comfy-xl">
            {/* // @TODO: bring back table of contents
            {hasJumpList && (
              <div className="Article__sidebar-sections Article__sidebar-box">
                <ArticleJumpNav {...article} />
              </div>
            )} */}
            {article.taxonomyArticleType !== keys.articleTypes.pressRelease && (
              <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start">
                {article.contributors && article.contributors.length > 0 && (
                  <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                    <h2 className="heading heading-xs">
                      {t('skippy.article.contributors')}
                    </h2>
                    <div className="Article__contributors flex flex-col gap-comfy-sm">
                      {article.contributors.map((contributor) => (
                        <div
                          key={contributor.name}
                          className="label-lg label-moderate flex gap-comfy-xs"
                        >
                          {contributor.image?.url && (
                            <Image
                              image={{
                                url: contributor.image.url,
                                alt: contributor.name,
                              }}
                              aspectRatio="1"
                              rounded="full"
                              className="Contributor__image"
                            />
                          )}
                          <div className="Contributor__info flex flex-col">
                            <h3 className="Contributor__heading label-md label-potent">
                              {contributor.name}
                            </h3>
                            <p>{contributor.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* // @TODO: SUBSCRIBE FORM */}
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                  <FormEmailSubscribe
                    className="max-w-md"
                    title={t('skippy.article.subscribeTitle')}
                    subscriptionCode="Leads_General_Sign_Up"
                    onSubmit={submitSubscription}
                    subscribeDirectionForm="column"
                    showInputsPlaceholder
                    titleSize="xs"
                  />
                </div>
              </div>
            )}

            {article.taxonomyArticleType === keys.articleTypes.pressRelease && (
              <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start">
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm border-b-1">
                  <h2 className="heading heading-xs pb-snug-lg">
                    {t('skippy.aboutCompassionTitle')}
                  </h2>
                  <div className="flex flex-col gap-comfy-xs body body-sm">
                    {t('skippy.aboutCompassionBody')}
                  </div>
                </div>
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                  <h2 className="heading heading-xs">
                    {t('skippy.socialMedia.follow')}
                  </h2>

                  {/* TODO: translate link titles */}
                  <div className="flex flex-col gap-comfy-lg">
                    <Link
                      href="https://www.facebook.com/compassionintl"
                      target="blank"
                      title="Compassion International - Facebook"
                    >
                      <ArkIcon iconName="facebook" size="24" />
                      {t('skippy.socialMedia.facebook')}
                    </Link>
                    <Link
                      href="https://twitter.com/compassion"
                      target="blank"
                      title="Compassion International - X (Twitter)"
                    >
                      <ArkIcon iconName="twitter" size="24" />
                      {t('skippy.socialMedia.twitter')}
                    </Link>
                    <Link
                      href="https://instagram.com/compassion"
                      target="blank"
                      title="Compassion International - Instagram"
                    >
                      <ArkIcon iconName="instagram" size="24" />
                      {t('skippy.socialMedia.instagram')}
                    </Link>
                    <Link
                      href="https://www.tiktok.com/@compassioninternational"
                      target="blank"
                      title="Compassion International - TikTok"
                    >
                      <ArkIcon iconName="tiktok" size="24" />
                      {t('skippy.socialMedia.tiktok')}
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {article.primaryCta &&
              ('fund' in article.primaryCta ? (
                <DonateShim {...{ ...article.primaryCta, body: undefined }} />
              ) : (
                <JumbotronNotable {...article.primaryCta} />
              ))}
          </aside>
        }
      />
      {activeSections.includes(ArticleSectionsEnum.enum.cta) && (
        <FlexibleSection
          meta={getSectionMeta(ArticleSectionsEnum.enum.cta)}
          children1={
            <JumbotronSidekick seoLevel={2} {...article.secondaryCta} />
          }
        />
      )}
      {activeSections.includes(ArticleSectionsEnum.enum.related) && (
        <FlexibleSection
          meta={getSectionMeta(ArticleSectionsEnum.enum.related)}
          children1={
            <>
              <BasicContentSolo
                seoLevel={2}
                title={t('skippy.article.relatedContent')}
              />
              <ArticleInsert article={article} seoLevel={3} />
            </>
          }
        />
      )}
      {activeSections.includes(ArticleSectionsEnum.enum.faq) && (
        <FlexibleSection
          meta={getSectionMeta(ArticleSectionsEnum.enum.faq)}
          children1={<AccordionMain seoLevel={2} {...article.faq} />}
        />
      )}
    </div>
  );
};
